import { useTranslation } from 'react-i18next'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@agion/ui'
import { Circle, Info } from 'lucide-react'
import { cn } from '@/core/infra_utils'
import type { HostGroupMember } from '@/core/infra_types'

interface Props {
  open: boolean
  onClose: () => void
  members: HostGroupMember[]
}

export function HostStatusPanel({ open, onClose, members }: Props) {
  const { t } = useTranslation(['pages'])
  const onlineCount = members.filter(m => m.hostStatus === 'online').length

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('pages:environments.lifecycle.hostsPanelTitle', 'Host Status')}</DialogTitle>
          <DialogDescription>
            {t('pages:environments.lifecycle.hostsPanelDescription', 'Review registered edge hosts and their current connection state.')}
          </DialogDescription>
        </DialogHeader>
        {members.length === 0 ? (
          <div className="py-4 space-y-3">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('pages:environments.lifecycle.noHostsRegistered', 'No hosts registered')}
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500 flex items-start gap-1.5">
              <Info size={12} className="mt-0.5 flex-shrink-0" />
              {t('pages:environments.lifecycle.noHostsHint', 'Hosts connect automatically after Edge Runtime installation. Check agent-host logs if hosts stay offline.')}
            </p>
          </div>
        ) : (
          <div className="py-2">
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              {onlineCount} / {members.length} {t('pages:environments.lifecycle.hostsOnline', 'online')}
            </p>
            <div className="space-y-2">
              {members.map(m => (
                <div key={m.hostId} className="flex items-center justify-between py-1.5 px-2 rounded-md bg-slate-50 dark:bg-slate-800/50">
                  <div className="flex items-center gap-2">
                    <Circle
                      size={8}
                      className={cn(
                        'fill-current',
                        m.hostStatus === 'online' ? 'text-emerald-500' : 'text-slate-400 dark:text-slate-500',
                      )}
                    />
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {m.hostName ?? m.hostId}
                    </span>
                  </div>
                  <span className={cn(
                    'text-xs',
                    m.hostStatus === 'online' ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500',
                  )}>
                    {m.hostStatus ?? t('pages:common.unknown', 'unknown')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
